import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { PasswordRecoveryModal } from "@/components/auth/PasswordRecoveryModal";
import { getBoardingUrl } from "@/config/preLaunchMode";
import { STORAGE_KEYS, removeStorageItem, setStorageItem } from "@/lib/storage";
import { toast } from "sonner";
import { Loader2, AlertCircle, UserPlus } from "lucide-react";

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signIn, user, loading: authLoading } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);

  // Check if user is already authenticated
  useEffect(() => {
    if (!authLoading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Handle invite token from URL query params
  useEffect(() => {
    const inviteEmail = searchParams.get("email");
    const inviteToken = searchParams.get("token");

    if (inviteEmail) {
      setEmail(decodeURIComponent(inviteEmail));
    }

    if (inviteToken) {
      // Store token for later use during sign up if needed
      sessionStorage.setItem("invite_token", inviteToken);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");

    // Validation
    if (!email.trim() || !password.trim()) {
      setErrorMessage("Please enter both email and password");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage("Please enter a valid email address");
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await signIn(email, password);

      if (error) {
        console.error("Login error:", error);

        // Handle specific error messages
        if (error.message.includes("Invalid login credentials")) {
          setErrorMessage("Invalid email or password. Please try again.");
        } else if (error.message.includes("Email not confirmed")) {
          setErrorMessage("Please confirm your email address before signing in.");
        } else if (error.message.includes("Email link is invalid")) {
          setErrorMessage("Your login link has expired. Please request a new one.");
        } else {
          setErrorMessage(error.message || "Failed to sign in. Please try again.");
        }
        return;
      }

      // Success - user will be redirected by the useEffect watching `user`
      toast.success("Welcome back!");

      // Store remember me preference (could be used for localStorage persistence)
      if (rememberMe) {
        setStorageItem(STORAGE_KEYS.REMEMBER_ME, "true");
      } else {
        removeStorageItem(STORAGE_KEYS.REMEMBER_ME);
      }
    } catch (error) {
      console.error("Unexpected login error:", error);
      setErrorMessage("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setShowRecoveryModal(true);
  };

  // Show loading spinner while checking auth
  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-2 border-blue-200 border-t-blue-500" />
          <p className="mt-4 text-sm font-medium text-slate-600 dark:text-slate-300">Preparing your workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <main className="relative flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-100 px-4 py-6 text-slate-700 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 dark:text-slate-200">
      {/* Background decorative elements */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 right-6 h-72 w-72 rounded-full bg-blue-200/60 blur-3xl dark:bg-blue-500/20" />
        <div className="absolute -bottom-20 left-0 h-80 w-80 rounded-full bg-indigo-200/60 blur-3xl dark:bg-indigo-500/20" />
        <div className="absolute inset-x-0 top-32 mx-auto h-64 max-w-3xl rounded-full bg-gradient-to-r from-blue-100/40 via-white/30 to-indigo-100/40 blur-3xl dark:from-blue-500/10 dark:via-slate-900/10 dark:to-indigo-500/10" />
      </div>

      <Card className="relative z-10 w-full max-w-xl overflow-hidden border border-blue-100/70 bg-white/80 shadow-2xl shadow-blue-100/40 backdrop-blur-xl transition dark:border-slate-800/70 dark:bg-slate-900/80 dark:shadow-none">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-sky-400 via-blue-500 to-indigo-500 dark:from-sky-500 dark:via-blue-500 dark:to-indigo-600" />

        <CardHeader className="space-y-2.5 pb-5 text-center">
          <div className="flex justify-center">
            <img
              src={`${import.meta.env.BASE_URL}logos/coordi-logo-horizontal.png`}
              alt="Coordi"
              className="h-24 w-auto drop-shadow-md"
            />
          </div>
          <div className="space-y-2">
            <CardTitle className="text-3xl font-semibold text-slate-900 dark:text-white">Welcome back</CardTitle>
            <CardDescription className="text-base text-slate-500 dark:text-slate-400">
              Sign in with your Coordi credentials to continue coordinating transactions.
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="space-y-5">
          <form onSubmit={handleSubmit} className="space-y-3" data-testid="login-form">
            {/* Email Field */}
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium text-slate-600 dark:text-slate-300">
                Email address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="your.email@example.com"
                data-testid="login-email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrorMessage("");
                }}
                disabled={isLoading}
                autoComplete="email"
                className={cn(
                  "h-11 rounded-lg bg-white/80 text-base shadow-sm transition focus-visible:ring-offset-2 dark:bg-slate-950/60",
                  errorMessage && "border-red-500 focus-visible:ring-red-500/40"
                )}
              />
            </div>

            {/* Password Field */}
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-sm font-medium text-slate-600 dark:text-slate-300">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                data-testid="login-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setErrorMessage("");
                }}
                disabled={isLoading}
                autoComplete="current-password"
                className={cn(
                  "h-11 rounded-lg bg-white/80 text-base shadow-sm transition focus-visible:ring-offset-2 dark:bg-slate-950/60",
                  errorMessage && "border-red-500 focus-visible:ring-red-500/40"
                )}
              />
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-blue-50/60 px-3 py-2 text-sm shadow-sm dark:bg-slate-900/70">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                  disabled={isLoading}
                />
                <label
                  htmlFor="remember"
                  className="cursor-pointer font-medium leading-none text-slate-600 transition hover:text-slate-800 dark:text-slate-300 dark:hover:text-white"
                >
                  Remember me
                </label>
              </div>
              <button
                type="button"
                onClick={handleForgotPassword}
                className="font-medium text-blue-600 transition hover:text-blue-700 hover:underline dark:text-sky-300 dark:hover:text-sky-200"
                disabled={isLoading}
              >
                Forgot password?
              </button>
            </div>

            {/* Error Message */}
            {errorMessage && (
              <div
                className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50/80 px-3.5 py-2.5 text-sm text-red-700 shadow-sm dark:border-red-400/40 dark:bg-red-500/10 dark:text-red-200"
                data-testid="login-error"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <p className="font-medium">{errorMessage}</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              className="relative h-11 w-full overflow-hidden rounded-lg bg-gradient-to-r from-sky-500 via-blue-500 to-indigo-500 text-base font-semibold text-white shadow-lg shadow-blue-200/60 transition hover:shadow-xl focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-80 dark:shadow-blue-900/50"
              disabled={isLoading}
              data-testid="login-submit"
            >
              {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {isLoading ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          {/* Sign Up Button */}
          <div className="space-y-3">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200 dark:border-slate-700" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white/80 px-2 text-slate-500 dark:bg-slate-900/80 dark:text-slate-400">
                  New to Coordi?
                </span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={() => window.location.href = getBoardingUrl()}
              className="h-11 w-full justify-center gap-2 rounded-lg border-slate-200 bg-white/80 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
            >
              <UserPlus className="h-4 w-4" />
              Create an Account
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Password Recovery Modal */}
      <PasswordRecoveryModal open={showRecoveryModal} onOpenChange={setShowRecoveryModal} />
    </main>
  );
};

export default Login;
